import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Space, Switch, Table, Tag, message } from 'antd';
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiClient } from '@/lib/api-client';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import type { ColumnsType } from 'antd/es/table';

type AslRow = {
  supplier_id: string;
  supplier_code?: string;
  supplier_name?: string;
  classification_type?: string;
  scope_category?: string;
  scope_of_approval?: string;
  previous_evaluation_date?: string | null;
  current_evaluation_date?: string | null;
  reevaluation_due_date?: string | null;
  asl_status?: string;
};

function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  if (response && typeof response === 'object') {
    const r = response as { data?: unknown };
    if (Array.isArray(r.data)) return r.data as T[];
  }
  return [];
}

function fmtDate(v?: string | null) {
  if (!v) return '—';
  return String(v).slice(0, 10);
}

function openAslPrint(rows: AslRow[]) {
  const body = rows
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${r.supplier_code || ''}</td>
      <td>${r.supplier_name || ''}</td>
      <td>${r.classification_type || ''}</td>
      <td>${r.scope_category || ''}</td>
      <td>${(r.scope_of_approval || '').replace(/</g, '&lt;')}</td>
      <td>${fmtDate(r.previous_evaluation_date)}</td>
      <td>${fmtDate(r.current_evaluation_date)}</td>
      <td>${fmtDate(r.reevaluation_due_date)}</td>
      <td>${r.asl_status || ''}</td>
    </tr>`
    )
    .join('');
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>Approved Supplier List</title>
    <style>
      body{font-family:Segoe UI,Arial,sans-serif;padding:24px}
      h1{font-size:18px;margin:0 0 4px}
      .meta{font-size:12px;margin-bottom:16px}
      table{border-collapse:collapse;width:100%;font-size:11px}
      th,td{border:1px solid #333;padding:5px;vertical-align:top}
      th{background:#f0f0f0;text-align:left}
    </style></head><body>
    <h1>APPROVED SUPPLIER LIST</h1>
    <div class="meta">Printed: ${new Date().toLocaleString()}</div>
    <table><thead><tr>
      <th>Sr.</th><th>Supplier ID</th><th>Supplier Name</th><th>Critical / Non Critical</th>
      <th>Scope category</th><th>Scope of approval</th>
      <th>Previous Evaluation</th><th>Current Evaluation</th><th>Re-Evaluation Due</th><th>Status</th>
    </tr></thead><tbody>${body}</tbody></table>
    </body></html>`);
  w.document.close();
  w.focus();
  w.print();
}

export const ApprovedSupplierListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AslRow[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/procurement/approved-supplier-list', {
        params: { include_inactive: includeInactive ? 'true' : 'false' },
      });
      setRows(unwrapList<AslRow>(res));
    } catch {
      message.error('Failed to load approved supplier list');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: ColumnsType<AslRow> = [
    { title: 'Supplier ID', dataIndex: 'supplier_code', width: 120 },
    { title: 'Supplier Name', dataIndex: 'supplier_name', width: 200 },
    {
      title: 'Criticality',
      dataIndex: 'classification_type',
      width: 120,
      render: (v) =>
        v ? <Tag color={v === 'Critical' ? 'red' : 'blue'}>{v}</Tag> : '—',
    },
    { title: 'Scope category', dataIndex: 'scope_category', width: 140, ellipsis: true },
    { title: 'Scope of approval', dataIndex: 'scope_of_approval', ellipsis: true },
    {
      title: 'Previous eval',
      dataIndex: 'previous_evaluation_date',
      width: 110,
      render: fmtDate,
    },
    {
      title: 'Current eval',
      dataIndex: 'current_evaluation_date',
      width: 110,
      render: fmtDate,
    },
    {
      title: 'Re-eval due',
      dataIndex: 'reevaluation_due_date',
      width: 110,
      render: fmtDate,
    },
    {
      title: 'Status',
      dataIndex: 'asl_status',
      width: 100,
      render: (v) => <Tag color={v === 'Active' ? 'green' : 'default'}>{v || '—'}</Tag>,
    },
  ];

  return (
    <div>
      <SmartBreadcrumbs />
      <EnterprisePageHeader
        documentLabel="Approved supplier list"
        subtitle="Active = approved + Qualified + active (existing supplier fields)"
        primaryActions={
          <Space>
            <span>
              Include inactive{' '}
              <Switch checked={includeInactive} onChange={setIncludeInactive} size="small" />
            </span>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              Refresh
            </Button>
            <Button type="primary" icon={<PrinterOutlined />} onClick={() => openAslPrint(rows)}>
              Print
            </Button>
          </Space>
        }
      />
      <WorkspaceLayout>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Read-model over existing supplier qualification"
          description="PO creation continues to use the existing approved + Qualified gate. This register does not add a parallel approval path."
        />
        <Table
          rowKey="supplier_id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 1100 }}
        />
      </WorkspaceLayout>
    </div>
  );
};

export default ApprovedSupplierListPage;
